/**
 * Holster Finder — Storefront API Edition
 *
 * Fetches all products from the Shopify Storefront API, parses JSON-array
 * metafields, builds a client-side filter index, and renders product cards
 * that match the theme's native card HTML structure.
 */

const PRODUCTS_PER_PAGE = 12;

const CARET_SVG = `<svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></svg>`;

const GRAPHQL_QUERY = `
query GetProducts($cursor: String) {
  products(first: 250, after: $cursor) {
    pageInfo {
      hasNextPage
      endCursor
    }
    edges {
      node {
        handle
        title
        availableForSale
        priceRange {
          minVariantPrice {
            amount
          }
        }
        images(first: 2) {
          edges {
            node {
              url
            }
          }
        }
        id
        reviewRating: metafield(namespace: "reviews", key: "rating") { value }
        reviewCount: metafield(namespace: "reviews", key: "rating_count") { value }
        make: metafield(namespace: "custom", key: "make") { value }
        model: metafield(namespace: "custom", key: "gunmodel") { value }
        holsterType: metafield(namespace: "custom", key: "holster_type") { value }
        light: metafield(namespace: "custom", key: "light") { value }
        lightManufacturer: metafield(namespace: "custom", key: "light_manufacturer") { value }
        lightModel: metafield(namespace: "custom", key: "light_model_") { value }
      }
    }
  }
}
`;

class HolsterFinderApp extends HTMLElement {
  connectedCallback() {
    // Read config from data attributes (set by Liquid)
    this.storefrontToken = this.dataset.storefrontToken;
    this.apiVersion = this.dataset.apiVersion || '2024-01';
    this.cardStyle = this.dataset.cardStyle || 'standard';
    this.cardColorScheme = this.dataset.cardColorScheme || 'scheme-1';
    this.badgePosition = this.dataset.badgePosition || 'bottom left';

    // Cache DOM elements
    this.makeSelect = this.querySelector('#finder-make');
    this.modelSelect = this.querySelector('#finder-model');
    this.typeGroup = this.querySelector('#finder-type-group');
    this.lightToggles = this.querySelectorAll('[data-light-toggle]');
    this.lightSection = this.querySelector('[data-step="4a"]');
    this.lightMakeSelect = this.querySelector('#finder-light-manufacturer');
    this.lightModelSelect = this.querySelector('#finder-light-model');
    this.resetButton = this.querySelector('.holster-finder__reset');
    this.countEl = this.querySelector('#HolsterFinderCount');
    this.gridEl = this.querySelector('#HolsterFinderGrid');
    this.paginationEl = this.querySelector('#HolsterFinderPagination');
    this.loadingEl = this.querySelector('#HolsterFinderLoading');
    this.initialEl = this.querySelector('#HolsterFinderInitial');
    this.emptyEl = this.querySelector('#HolsterFinderEmpty');

    // State
    this.state = {
      make: null,
      model: null,
      type: null,
      hasLight: null,
      lightMake: null,
      lightModel: null
    };

    // Pagination state
    this.currentPage = 1;

    // Product data
    this.allProducts = [];
    this.makes = [];
    this.makeToModels = {};

    // Debounced filter apply
    this.debouncedApply = this.debounce(this.applyFilters.bind(this), 50);

    // Show loading spinner immediately; initial/empty stay hidden until products load
    this.showLoading(true);
    this.showInitial(false);
    this.showEmpty(false);

    this.loadProducts();
  }

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  async loadProducts() {
    this.showLoading(true);

    try {
      const raw = await this.fetchAllProducts();
      this.allProducts = this.processProducts(raw);
      this.buildIndex();
      this.populateMakeDropdown();
      this.populateTypeButtons();
      this.bindEvents();
      this.showLoading(false);
      this.showInitial(true);
      this.updateCount(this.allProducts.length);
    } catch (err) {
      console.error('[HolsterFinder] Failed to load products:', err);
      this.showLoading(false);
      if (this.loadingEl) {
        this.loadingEl.innerHTML = '<p>Unable to load products. Please refresh the page.</p>';
        this.loadingEl.hidden = false;
      }
    }
  }

  async fetchAllProducts() {
    const endpoint = `https://fourbrothersinc.com/api/${this.apiVersion}/graphql.json`;
    const headers = {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': this.storefrontToken
    };

    const allNodes = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      const variables = cursor ? { cursor } : {};
      const res = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ query: GRAPHQL_QUERY, variables })
      });

      if (!res.ok) throw new Error(`API error ${res.status}`);

      const json = await res.json();
      if (json.errors) throw new Error(json.errors[0].message);

      const productsData = json.data.products;
      productsData.edges.forEach(edge => allNodes.push(edge.node));

      hasNextPage = productsData.pageInfo.hasNextPage;
      cursor = productsData.pageInfo.endCursor;
    }

    return allNodes;
  }

  // ---------------------------------------------------------------------------
  // Data processing
  // ---------------------------------------------------------------------------

  parseMetafieldValue(val) {
    if (val === null || val === undefined || val === '') return [];
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed.map(s => String(s).trim()).filter(Boolean);
      return [String(parsed).trim()].filter(Boolean);
    } catch {
      return [String(val).trim()].filter(Boolean);
    }
  }

  processProducts(rawProducts) {
    const results = [];

    for (const node of rawProducts) {
      if (!node.availableForSale) continue;

      const makes = this.parseMetafieldValue(node.make ? node.make.value : null);
      const models = this.parseMetafieldValue(node.model ? node.model.value : null);
      const types = this.parseMetafieldValue(node.holsterType ? node.holsterType.value : null);

      // Skip products missing required metafields
      if (makes.length === 0 || models.length === 0 || types.length === 0) continue;

      const lightRaw = node.light ? node.light.value : '';
      const hasLight = /^(true|yes)$/i.test(lightRaw.trim());

      const lightMakers = this.parseMetafieldValue(
        node.lightManufacturer ? node.lightManufacturer.value : null
      );
      const lightModels = this.parseMetafieldValue(
        node.lightModel ? node.lightModel.value : null
      );

      const priceAmount = parseFloat(node.priceRange.minVariantPrice.amount);
      const imageEdges = node.images ? node.images.edges : [];
      const imageUrl = imageEdges[0] ? imageEdges[0].node.url : null;
      const secondImageUrl = imageEdges[1] ? imageEdges[1].node.url : null;
      // Parse numeric ID from GID (e.g. "gid://shopify/Product/123456789")
      const productId = node.id ? node.id.split('/').pop() : null;

      // Parse review rating metafield (JSON: {"value":4.8,"scale_min":1.0,"scale_max":5.0})
      let reviewRating = null;
      let reviewScaleMax = 5;
      if (node.reviewRating && node.reviewRating.value) {
        try {
          const r = JSON.parse(node.reviewRating.value);
          reviewRating = parseFloat(r.value);
          reviewScaleMax = parseFloat(r.scale_max) || 5;
        } catch { /* no rating */ }
      }
      const reviewCount = node.reviewCount ? parseInt(node.reviewCount.value, 10) : null;

      results.push({
        handle: node.handle,
        title: node.title,
        price: priceAmount,
        imageUrl,
        secondImageUrl,
        productId,
        reviewRating,
        reviewScaleMax,
        reviewCount,
        makes,
        models,
        types,
        hasLight,
        lightMakers,
        lightModels
      });
    }

    return results;
  }

  buildIndex() {
    const makeSet = new Set();
    const makeToModels = {};

    for (const p of this.allProducts) {
      for (const make of p.makes) {
        makeSet.add(make);
        if (!makeToModels[make]) makeToModels[make] = new Set();
        for (const model of p.models) {
          makeToModels[make].add(model);
        }
      }
    }

    this.makes = [...makeSet].sort((a, b) => a.localeCompare(b));
    this.makeToModels = {};
    for (const [make, modelSet] of Object.entries(makeToModels)) {
      this.makeToModels[make] = [...modelSet].sort((a, b) => a.localeCompare(b));
    }
  }

  // ---------------------------------------------------------------------------
  // Dropdown population
  // ---------------------------------------------------------------------------

  populateMakeDropdown() {
    if (!this.makeSelect) return;
    this.makeSelect.innerHTML = '<option value="">Select Make…</option>';
    for (const make of this.makes) {
      const opt = document.createElement('option');
      opt.value = make;
      opt.textContent = make;
      this.makeSelect.appendChild(opt);
    }
  }

  populateTypeButtons() {
    if (!this.typeGroup) return;
    const typeSet = new Set();
    for (const p of this.allProducts) {
      for (const t of p.types) typeSet.add(t);
    }
    const types = [...typeSet].sort((a, b) => a.localeCompare(b));
    this.typeGroup.innerHTML = '';
    for (const type of types) {
      const label = document.createElement('label');
      label.className = 'holster-finder__radio';
      label.innerHTML = `
        <input type="radio" name="holster-type" value="${this.escapeAttr(type)}">
        <span class="holster-finder__radio-label">${this.escapeHtml(type)}</span>
      `;
      this.typeGroup.appendChild(label);
    }
    // Re-cache type radios after populating
    this.typeRadios = this.querySelectorAll('[name="holster-type"]');
  }

  updateModelDropdown(make) {
    if (!this.modelSelect) return;
    const models = make ? (this.makeToModels[make] || []) : [];

    if (models.length === 0) {
      this.modelSelect.disabled = true;
      this.modelSelect.innerHTML = '<option value="">First select a make…</option>';
      return;
    }

    this.modelSelect.innerHTML = '<option value="">Select Model…</option>';
    for (const model of models) {
      const opt = document.createElement('option');
      opt.value = model;
      opt.textContent = model;
      this.modelSelect.appendChild(opt);
    }
    this.modelSelect.disabled = false;
  }

  updateScopedLightDropdowns() {
    // Get products filtered by make + model + type + hasLight only (no light make/model)
    const preLightFiltered = this.allProducts.filter(p => {
      if (this.state.make && !p.makes.includes(this.state.make)) return false;
      if (this.state.model && !p.models.includes(this.state.model)) return false;
      if (this.state.type && !p.types.includes(this.state.type)) return false;
      if (this.state.hasLight === true && !p.hasLight) return false;
      if (this.state.hasLight === false && p.hasLight) return false;
      return true;
    });

    // Scoped light makes
    const lightMakeSet = new Set();
    for (const p of preLightFiltered) {
      for (const lm of p.lightMakers) lightMakeSet.add(lm);
    }
    const scopedLightMakes = [...lightMakeSet].sort((a, b) => a.localeCompare(b));

    // Repopulate light manufacturer dropdown
    if (this.lightMakeSelect) {
      const prevMake = this.lightMakeSelect.value;
      this.lightMakeSelect.innerHTML = '<option value="">Select Light Manufacturer…</option>';
      for (const lm of scopedLightMakes) {
        const opt = document.createElement('option');
        opt.value = lm;
        opt.textContent = lm;
        this.lightMakeSelect.appendChild(opt);
      }
      // Preserve selection if still valid
      if (prevMake && scopedLightMakes.includes(prevMake)) {
        this.lightMakeSelect.value = prevMake;
      } else if (prevMake) {
        // Previous selection no longer valid — clear it
        this.state.lightMake = null;
        this.state.lightModel = null;
      }
    }

    // Scoped light models (only if light make is selected)
    if (this.lightModelSelect) {
      if (!this.state.lightMake) {
        this.lightModelSelect.disabled = true;
        this.lightModelSelect.innerHTML = '<option value="">First select light manufacturer…</option>';
        return;
      }

      const lightModelSet = new Set();
      for (const p of preLightFiltered) {
        if (!p.lightMakers.includes(this.state.lightMake)) continue;
        for (const lmod of p.lightModels) lightModelSet.add(lmod);
      }
      const scopedLightModels = [...lightModelSet].sort((a, b) => a.localeCompare(b));

      const prevModel = this.lightModelSelect.value;
      this.lightModelSelect.innerHTML = '<option value="">Select Light Model…</option>';

      if (scopedLightModels.length > 0) {
        for (const lmod of scopedLightModels) {
          const opt = document.createElement('option');
          opt.value = lmod;
          opt.textContent = lmod;
          this.lightModelSelect.appendChild(opt);
        }
        this.lightModelSelect.disabled = false;
        if (prevModel && scopedLightModels.includes(prevModel)) {
          this.lightModelSelect.value = prevModel;
        } else if (prevModel) {
          this.state.lightModel = null;
        }
      } else {
        // No light models available (all products for this light make have empty lightModel)
        this.lightModelSelect.innerHTML = '<option value="">No models available</option>';
        this.lightModelSelect.disabled = true;
        this.state.lightModel = null;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Filtering
  // ---------------------------------------------------------------------------

  getFilteredProducts() {
    return this.allProducts.filter(p => {
      if (this.state.make && !p.makes.includes(this.state.make)) return false;
      if (this.state.model && !p.models.includes(this.state.model)) return false;
      if (this.state.type && !p.types.includes(this.state.type)) return false;
      if (this.state.hasLight === true && !p.hasLight) return false;
      if (this.state.hasLight === false && p.hasLight) return false;
      if (this.state.lightMake && !p.lightMakers.includes(this.state.lightMake)) return false;
      if (this.state.lightModel && !p.lightModels.includes(this.state.lightModel)) return false;
      return true;
    });
  }

  applyFilters() {
    const filtered = this.getFilteredProducts();

    // Update scoped light dropdowns before rendering
    if (this.state.hasLight === true && this.lightSection) {
      this.updateScopedLightDropdowns();
    }

    this.renderProducts(filtered);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  renderProducts(products) {
    // Determine what to show
    const hasAnyFilter = this.state.make || this.state.type;

    if (!hasAnyFilter) {
      this.showInitial(true);
      this.showEmpty(false);
      this.gridEl.innerHTML = '';
      this.clearPagination();
      this.updateCount(this.allProducts.length);
      return;
    }

    this.showInitial(false);

    if (products.length === 0) {
      this.showEmpty(true);
      this.gridEl.innerHTML = '';
      this.clearPagination();
      this.updateCount(0);
      return;
    }

    this.showEmpty(false);

    const totalPages = Math.ceil(products.length / PRODUCTS_PER_PAGE);

    // Clamp currentPage to valid range
    if (this.currentPage > totalPages) this.currentPage = totalPages;
    if (this.currentPage < 1) this.currentPage = 1;

    const start = (this.currentPage - 1) * PRODUCTS_PER_PAGE;
    const pageProducts = products.slice(start, start + PRODUCTS_PER_PAGE);

    this.gridEl.innerHTML = pageProducts.map(p => this.buildCardHTML(p)).join('');
    this.renderPagination(products.length, totalPages);
    this.updateCount(products.length);
  }

  clearPagination() {
    if (this.paginationEl) this.paginationEl.innerHTML = '';
  }

  renderPagination(_totalProducts, totalPages) {
    if (!this.paginationEl) return;
    this.paginationEl.innerHTML = this.buildPaginationHTML(totalPages);

    // Bind page button clicks
    this.paginationEl.querySelectorAll('[data-page]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.currentPage = parseInt(btn.dataset.page, 10);
        this.applyFilters();
        // Scroll to the top of the product grid
        this.gridEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  buildPaginationHTML(totalPages) {
    if (totalPages <= 1) return '';

    const current = this.currentPage;
    let items = '';

    // Previous arrow — matches Shopify's pagination__item--next class (caret rotated 90deg = left)
    if (current > 1) {
      items += `<li>
          <button type="button" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" data-page="${current - 1}" aria-label="Previous page">
            ${CARET_SVG}
          </button>
        </li>`;
    }

    // Page number links with ellipsis
    for (const part of this.getPaginationParts(current, totalPages)) {
      if (part === '...') {
        items += `<li><span class="pagination__item">…</span></li>`;
      } else if (part === current) {
        items += `<li>
            <a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${part}">${part}</a>
          </li>`;
      } else {
        items += `<li>
            <button type="button" class="pagination__item link" data-page="${part}" aria-label="Page ${part}">${part}</button>
          </li>`;
      }
    }

    // Next arrow — matches Shopify's pagination__item--prev class (caret rotated -90deg = right)
    if (current < totalPages) {
      items += `<li>
          <button type="button" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" data-page="${current + 1}" aria-label="Next page">
            ${CARET_SVG}
          </button>
        </li>`;
    }

    return `<div class="pagination-wrapper">
      <nav class="pagination" role="navigation" aria-label="Pagination">
        <ul class="pagination__list list-unstyled" role="list">
          ${items}
        </ul>
      </nav>
    </div>`;
  }

  getPaginationParts(current, total) {
    // Show all pages when there are 7 or fewer
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const delta = 2; // pages shown on each side of current
    const parts = [1];

    const rangeStart = Math.max(2, current - delta);
    const rangeEnd = Math.min(total - 1, current + delta);

    if (rangeStart > 2) parts.push('...');
    for (let i = rangeStart; i <= rangeEnd; i++) parts.push(i);
    if (rangeEnd < total - 1) parts.push('...');

    parts.push(total);
    return parts;
  }

  buildCardHTML(product) {
    const href = `/products/${product.handle}`;
    const title = this.escapeHtml(product.title);
    const price = this.formatPrice(product.price);
    const colorScheme = this.cardColorScheme;

    // Image section
    let mediaHTML = '';
    if (product.imageUrl) {
      const imgSrc = product.imageUrl.includes('?')
        ? `${product.imageUrl}&width=533`
        : `${product.imageUrl}?width=533`;

      let secondImgHTML = '';
      if (product.secondImageUrl) {
        const imgSrc2 = product.secondImageUrl.includes('?')
          ? `${product.secondImageUrl}&width=533`
          : `${product.secondImageUrl}?width=533`;
        secondImgHTML = `
            <img
              src="${this.escapeAttr(imgSrc2)}"
              alt="${title}"
              loading="lazy"
              class="motion-reduce"
            >`;
      }

      mediaHTML = `
        <div class="card__media">
          <div class="media media--transparent media--hover-effect">
            <img
              src="${this.escapeAttr(imgSrc)}"
              alt="${title}"
              loading="lazy"
              class="motion-reduce"
            >${secondImgHTML}
          </div>
        </div>`;
    }

    // Build Judge.me badge HTML using rating data (matches Judge.me CSS exactly)
    let ratingHTML = '';
    if (product.reviewRating !== null && product.reviewCount) {
      const rating = product.reviewRating;
      const full = Math.floor(rating);
      const half = (rating % 1) >= 0.3 ? 1 : 0;
      const empty = 5 - full - half;
      const stars =
        '<span class="jdgm-star jdgm--on"></span>'.repeat(full) +
        (half ? '<span class="jdgm-star jdgm--half"></span>' : '') +
        '<span class="jdgm-star jdgm--off"></span>'.repeat(empty);
      const countLabel = `${product.reviewCount} review${product.reviewCount !== 1 ? 's' : ''}`;
      ratingHTML = `
        <div class="jdgm-widget jdgm-preview-badge" data-id="${this.escapeAttr(product.productId)}" data-auto-install="false">
          <div class="jdgm-prev-badge" data-average-rating="${rating}" data-number-of-reviews="${product.reviewCount}">
            <span class="jdgm-prev-badge__stars" data-score="${rating}" aria-label="${this.escapeAttr(`${rating} stars`)}">${stars}</span>
            <span class="jdgm-prev-badge__text">${this.escapeHtml(countLabel)}</span>
          </div>
        </div>`;
    }

    return `
      <li class="grid__item">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--${this.escapeAttr(this.cardStyle)} card--media" style="--ratio-percent: 100%;">
            <div class="card__inner color-${this.escapeAttr(colorScheme)} gradient ratio" style="--ratio-percent: 100%;">
              ${mediaHTML}
              <div class="card__content">
                <div class="card__information">
                  <h3 class="card__heading">
                    <a href="${href}" class="full-unstyled-link">${title}</a>
                  </h3>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${href}" class="full-unstyled-link">${title}</a>
                </h3>
                <div class="card-information">
                  ${ratingHTML}
                  <div class="price">
                    <div class="price__container">
                      <span class="price-item price-item--regular">${price}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>`;
  }

  updateCount(count) {
    if (!this.countEl) return;
    if (count === 0) {
      this.countEl.textContent = 'No holsters match';
    } else if (this.state.make || this.state.type) {
      this.countEl.textContent = `${count} holster${count !== 1 ? 's' : ''} found`;
    } else {
      this.countEl.textContent = `${count} holsters available`;
    }
  }

  // ---------------------------------------------------------------------------
  // UI state helpers
  // ---------------------------------------------------------------------------

  showLoading(show) {
    if (!this.loadingEl) return;
    this.loadingEl.style.display = show ? 'flex' : 'none';
  }

  showInitial(show) {
    if (!this.initialEl) return;
    this.initialEl.style.display = show ? 'flex' : 'none';
  }

  showEmpty(show) {
    if (!this.emptyEl) return;
    this.emptyEl.style.display = show ? 'flex' : 'none';
  }

  // ---------------------------------------------------------------------------
  // Event binding
  // ---------------------------------------------------------------------------

  bindEvents() {
    if (this.makeSelect) {
      this.makeSelect.addEventListener('change', this.onMakeChange.bind(this));
    }
    if (this.modelSelect) {
      this.modelSelect.addEventListener('change', this.onModelChange.bind(this));
    }

    // Type radio buttons — bind to parent group since buttons are generated dynamically
    if (this.typeGroup) {
      this.typeGroup.addEventListener('change', this.onTypeChange.bind(this));
      // Allow clicking a checked radio to deselect it
      this.typeGroup.addEventListener('click', (e) => {
        const input = e.target.closest('input[type="radio"]');
        if (!input) return;
        if (this.state.type === input.value) {
          input.checked = false;
          this.state.type = null;
          this.currentPage = 1;
          this.debouncedApply();
        }
      });
    }

    this.lightToggles.forEach(toggle => {
      toggle.addEventListener('click', this.onLightToggle.bind(this));
    });

    if (this.lightMakeSelect) {
      this.lightMakeSelect.addEventListener('change', this.onLightMakeChange.bind(this));
    }
    if (this.lightModelSelect) {
      this.lightModelSelect.addEventListener('change', this.onLightModelChange.bind(this));
    }
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onReset.bind(this));
    }
  }

  // ---------------------------------------------------------------------------
  // Event handlers
  // ---------------------------------------------------------------------------

  onMakeChange(e) {
    this.state.make = e.target.value || null;
    this.state.model = null;
    this.state.lightMake = null;
    this.state.lightModel = null;
    this.currentPage = 1;
    this.updateModelDropdown(this.state.make);
    this.debouncedApply();
  }

  onModelChange(e) {
    this.state.model = e.target.value || null;
    this.state.lightMake = null;
    this.state.lightModel = null;
    this.currentPage = 1;
    this.debouncedApply();
  }

  onTypeChange(e) {
    const input = e.target.closest('input[type="radio"]');
    if (!input) return;
    this.state.type = input.value || null;
    this.currentPage = 1;
    this.debouncedApply();
  }

  onLightToggle(e) {
    const hasLight = e.currentTarget.dataset.lightToggle === 'yes';
    this.state.hasLight = hasLight;
    this.currentPage = 1;

    this.lightToggles.forEach(t => {
      t.classList.toggle('active', t.dataset.lightToggle === (hasLight ? 'yes' : 'no'));
    });

    if (this.lightSection) {
      if (hasLight) {
        this.lightSection.classList.remove('holster-finder__step--hidden');
      } else {
        this.lightSection.classList.add('holster-finder__step--hidden');
        this.state.lightMake = null;
        this.state.lightModel = null;
        if (this.lightMakeSelect) this.lightMakeSelect.value = '';
        if (this.lightModelSelect) {
          this.lightModelSelect.disabled = true;
          this.lightModelSelect.innerHTML = '<option value="">First select light manufacturer…</option>';
        }
      }
    }

    this.debouncedApply();
  }

  onLightMakeChange(e) {
    this.state.lightMake = e.target.value || null;
    this.state.lightModel = null;
    this.currentPage = 1;
    if (this.lightModelSelect) {
      this.lightModelSelect.value = '';
    }
    this.debouncedApply();
  }

  onLightModelChange(e) {
    this.state.lightModel = e.target.value || null;
    this.currentPage = 1;
    this.debouncedApply();
  }

  onReset() {
    this.state = {
      make: null,
      model: null,
      type: null,
      hasLight: null,
      lightMake: null,
      lightModel: null
    };
    this.currentPage = 1;

    if (this.makeSelect) this.makeSelect.value = '';
    if (this.modelSelect) {
      this.modelSelect.disabled = true;
      this.modelSelect.innerHTML = '<option value="">First select a make…</option>';
    }

    // Uncheck all type radios
    this.querySelectorAll('[name="holster-type"]').forEach(r => (r.checked = false));

    this.lightToggles.forEach(t => t.classList.remove('active'));

    if (this.lightSection) {
      this.lightSection.classList.add('holster-finder__step--hidden');
    }
    if (this.lightMakeSelect) this.lightMakeSelect.value = '';
    if (this.lightModelSelect) {
      this.lightModelSelect.disabled = true;
      this.lightModelSelect.innerHTML = '<option value="">First select light manufacturer…</option>';
    }

    this.showInitial(true);
    this.showEmpty(false);
    this.gridEl.innerHTML = '';
    this.updateCount(this.allProducts.length);
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  formatPrice(amount) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  }
}

if (!customElements.get('holster-finder-app')) {
  customElements.define('holster-finder-app', HolsterFinderApp);
}
